// =============================================================================
// Telegram Mini App SDK helpers.
//
// Используем нативный window.Telegram.WebApp (без внешних SDK) — он
// загружается скриптом https://telegram.org/js/telegram-web-app.js, который
// мы подключаем в layout. Это минимальная зависимость для MVP.
//
// Для dev-режима вне Telegram: можно передать ?initData=... в URL.
// =============================================================================

interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    start_param?: string;
  };
  ready: () => void;
  expand: () => void;
  close: () => void;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  openTelegramLink?: (url: string) => void;
  openInvoice?: (url: string, callback?: (status: string) => void) => void;
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  MainButton?: {
    text: string;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    setParams: (params: { text?: string; color?: string; is_visible?: boolean; is_active?: boolean }) => void;
  };
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null;
  return window.Telegram?.WebApp ?? null;
}

export function getInitData(): string | null {
  if (typeof window === 'undefined') return null;
  // 1) Реальный TG WebApp
  const fromTg = window.Telegram?.WebApp?.initData;
  if (fromTg) return fromTg;
  // 2) Dev override через URL parameter
  const url = new URL(window.location.href);
  return url.searchParams.get('initData');
}

/**
 * Парсит start_param из Telegram WebApp initDataUnsafe.
 * Также проверяет URL search params (?startapp=... / ?start=...) для dev-режима.
 *
 * Telegram передаёт start_param когда юзер открыл бота через
 * `t.me/<bot>?start=<param>` или `t.me/<bot>?startapp=<param>`.
 */
export function getStartParam(): string | null {
  if (typeof window === 'undefined') return null;
  const tg = getWebApp();
  if (tg?.initDataUnsafe?.start_param) return tg.initDataUnsafe.start_param;
  const qs = new URLSearchParams(window.location.search);
  return qs.get('startapp') ?? qs.get('start') ?? null;
}

/**
 * Извлекает market_id из deep link параметра вида `market_<id>` или `market<id>`.
 * Возвращает null если param не market-deeplink.
 */
export function parseMarketStartParam(param: string | null): string | null {
  if (!param) return null;
  const match = /^market[_]?([A-Za-z0-9_-]{4,32})$/.exec(param);
  return match?.[1] ?? null;
}

export function isInTelegram(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(window.Telegram?.WebApp?.initData);
}

export function hapticImpact(style: 'light' | 'medium' | 'heavy' = 'light'): void {
  getWebApp()?.HapticFeedback?.impactOccurred(style);
}

export function hapticNotification(type: 'success' | 'error' | 'warning'): void {
  getWebApp()?.HapticFeedback?.notificationOccurred(type);
}

/** Открыть ссылку в Telegram (например, deep-link на бота с ?start=deposit). */
export function openTelegramLink(url: string): void {
  const webApp = getWebApp();
  if (webApp?.openTelegramLink) {
    webApp.openTelegramLink(url);
  } else {
    // Fallback вне Telegram
    window.open(url, '_blank');
  }
}
